import type { SpaceObjectFormatter, SstFormatter } from '@entities/SpaceObject/responseFormatters';
import type { SpaceObjectFront } from '@entities/SpaceObject/type';
import { SST_TYPES } from '@root/src/entities/Sst/sstType/enum';
import type {
  SstBack,
  SstFront,
  SstEventFront,
  SstEventBack,
  SstCommentBack,
  SstCommentFront,
  Trajectory,
  Operation,
  TrajectoryBack,
  OperationBack,
  OrbitInformationBack,
} from '@root/src/entities/Sst/type';
import { parseUTCDate } from '@root/src/utils/formatters/dates';

const formatSingleSstEvent = (sst: SstEventBack): SstEventFront => {
  switch (sst.sstType) {
    case SST_TYPES.TRAJECTORY: {
      const trajectory = sst as TrajectoryBack;
      const { orbit, effectiveAreaMassRatio } = trajectory;
      const { epoch, accuracy, originalOrbit, originalOrbitType, ephemerisOrbit, ...orbitProps } = (orbit ||
        {}) as OrbitInformationBack;
      const parsedEpoch = epoch?.trim() ? parseUTCDate(epoch) : undefined;

      return {
        sstType: SST_TYPES.TRAJECTORY,
        // Keep only non-orbital values at root
        effectiveAreaMassRatio,
        // Put all orbital values under orbit
        orbit: {
          ...orbitProps,
          epoch: parsedEpoch,
          accuracy,
          originalOrbit,
          originalOrbitType,
          ephemerisOrbit,
        },
      } as Trajectory;
    }

    case SST_TYPES.OPERATION: {
      const operation = sst as OperationBack;
      const { orbit, operationDate, operationType, operationData, comment } = operation;
      const { epoch, accuracy, originalOrbit, originalOrbitType, ephemerisOrbit, ...orbitProps } = (orbit ||
        {}) as OrbitInformationBack;
      const parsedEpoch = epoch?.trim() ? parseUTCDate(epoch) : undefined;

      return {
        sstType: SST_TYPES.OPERATION,
        operationType,
        operationDate: operationDate?.trim() ? parseUTCDate(operationDate) : undefined,
        operationData,
        comment,
        // Put all orbital values under orbit
        orbit: {
          ...orbitProps,
          epoch: parsedEpoch,
          accuracy,
          originalOrbit,
          originalOrbitType,
          ephemerisOrbit,
        },
      } as Operation;
    }

    case SST_TYPES.STATUS: {
      const status = sst;
      return {
        ...status,
        deorbitedStatus: status.deorbitedStatus
          ? {
              ...status.deorbitedStatus,
              deorbitationDate: status.deorbitedStatus.deorbitationDate
                ? parseUTCDate(status.deorbitedStatus.deorbitationDate)
                : undefined,
            }
          : undefined,
        destroyedStatus: status.destroyedStatus
          ? {
              ...status.destroyedStatus,
              destructionDate: status.destroyedStatus.destructionDate
                ? parseUTCDate(status.destroyedStatus.destructionDate)
                : undefined,
            }
          : undefined,
      };
    }

    default:
      return sst as SstEventFront;
  }
};

/**
 * Builds the block formatter. The object a block is about is formatted by
 * `formatSpaceObject`, handed in rather than imported: that formatter embeds
 * the object's latest blocks through this one, and the knot is tied in
 * `src/entities/responseFormatters.ts` so neither module imports the other.
 */
export const createSstFromBack =
  (formatSpaceObject: SpaceObjectFormatter): SstFormatter =>
  ({
    _id,
    createdAt,
    updatedAt,
    sstEvents,
    spaceObject,
    comments,
    unknownSpaceObjectId,
    ...rest
  }: SstBack): SstFront => {
    // Handle spaceObject - it can be either a string ID or a full object
    let spaceObjectId: string | undefined;
    let processedSpaceObject: SpaceObjectFront | undefined;

    if (spaceObject) {
      if (typeof spaceObject === 'string') {
        // If it's just a string, treat it as an ID
        spaceObjectId = spaceObject;
      } else if (typeof spaceObject === 'object') {
        // If it's an object, process it and also extract the ID
        try {
          processedSpaceObject = formatSpaceObject(spaceObject);
          spaceObjectId = processedSpaceObject.id || spaceObject._id;
        } catch {
          // Fallback to using _id if processing fails
          spaceObjectId = spaceObject._id;
        }
      }
    }

    return {
      ...rest,
      id: _id,
      createdAt: createdAt ? parseUTCDate(createdAt) : new Date(0),
      updatedAt: updatedAt?.trim() ? parseUTCDate(updatedAt) : undefined,
      spaceObjectId,
      spaceObject: processedSpaceObject,
      unknownSpaceObjectId,
      ...(Array.isArray(sstEvents) && {
        sstEvents: sstEvents.map(formatSingleSstEvent),
      }),
      comments: Array.isArray(comments) ? comments.map(sstCommentFromBack) : [],
    };
  };

export const sstCommentFromBack = ({ _id, timestamp, ...rest }: SstCommentBack): SstCommentFront => ({
  id: _id,
  timestamp: timestamp ? parseUTCDate(timestamp) : new Date(0),
  ...rest,
});
